import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
import { useCallback, useEffect, useState } from 'react';
import {
  Plus, Loader2, Download, ArrowLeft, Coins, ListChecks, LayoutGrid, ChartNoAxesColumnIncreasing,
} from 'lucide-react';
import { salesTargetsAPI, incentiveAPI, blobErrorMessage } from '../../services/api';
import { downloadXlsx } from '../../utils/download';
import { PageErrorBanner } from '../../components/PageErrorBanner';
import { BlockLoader } from '../../components/loading';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import Modal from '../../components/Modal';
import IncentivePlanEditor from './IncentivePlanEditor';
import IncentivePayout from './IncentivePayout';
import DesignationMultiplierCard from './DesignationMultiplierCard';
import { describePerUnitPlan } from './perUnit';
import {
  formatMoney, isPerUnitPlan,
  type IncentivePlan, type IncentivePlanKind, type MultiplierField, type SalesTargetConfig,
  type CoordinateOption, type SalesField,
} from './types';

/**
 * Incentive — what somebody is PAID, from what they were asked to sell and what
 * they actually sold.
 *
 * ── TWO HALVES, DELIBERATELY SEPARATED ───────────────────────────────────────
 * Plans are configuration: rare, careful, admin-only edits to a grid that decides
 * money. The Payout is operational: read every month, by more people, and never
 * edited. Putting them behind one tab strip keeps the rare destructive thing out
 * of the way of the frequent read — the same reasoning that moved Running Stock
 * out of the Products page.
 *
 * ── NOTHING IS STORED ────────────────────────────────────────────────────────
 * There is no "run payroll" button that freezes a number. Every figure on the
 * Payout view is recalculated from the plan, the targets and the sales as they
 * stand right now, so correcting a sale corrects the payout without anyone
 * remembering to re-run anything.
 *
 * ── TWO KINDS OF PLAN ────────────────────────────────────────────────────────
 * A grid pays fixed amounts by % of target, product by product; a per-unit plan
 * pays a rate for every unit sold, rising in steps. The kind is asked first and
 * fixed for the plan's life — the two have nothing to carry over between them.
 */

/** The two kinds, as the cards that ask which one a new plan is. */
const PLAN_KINDS: { kind: IncentivePlanKind; title: string; detail: string; Icon: typeof LayoutGrid }[] = [
  { kind: 'grid', title: 'Grid', detail: 'Fixed amounts by % of target, per product', Icon: LayoutGrid },
  { kind: 'per_unit', title: 'Per unit', detail: '₹ for every unit sold, rising in steps', Icon: ChartNoAxesColumnIncreasing },
];

interface Props {
  projectId: number;
  month: string;
  config: SalesTargetConfig;
}

type View = 'plans' | 'payout';

export default function IncentiveTab({ projectId, month, config }: Props) {
  const [view, setView] = useState<View>('payout');
  const [plans, setPlans] = useState<IncentivePlan[]>([]);
  const [coordinates, setCoordinates] = useState<CoordinateOption[]>([]);
  const [salesFields, setSalesFields] = useState<SalesField[]>([]);
  const [storeFields, setStoreFields] = useState<MultiplierField[]>([]);
  const [employeeFields, setEmployeeFields] = useState<MultiplierField[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<IncentivePlan | null>(null);
  const [creating, setCreating] = useState(false);
  // The "which kind?" window: closed (null), open with nothing picked yet, or a pick.
  const [askKind, setAskKind] = useState<null | { kind: IncentivePlanKind | null }>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [planRes, coordRes, fieldRes, storeRes, empRes] = await Promise.all([
        incentiveAPI.listPlans(projectId, { includeInactive: true }),
        salesTargetsAPI.listCoordinates(projectId, config.productGranularity),
        salesTargetsAPI.listSalesFields(projectId),
        salesTargetsAPI.listMultiplierFields(projectId, 'store'),
        salesTargetsAPI.listMultiplierFields(projectId, 'employee'),
      ]);
      setPlans(planRes.data.data ?? []);
      setCoordinates(coordRes.data.data ?? []);
      setSalesFields(fieldRes.data.data ?? []);
      setStoreFields(storeRes.data.data ?? []);
      setEmployeeFields(empRes.data.data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load the incentive plans.');
    } finally {
      setLoading(false);
    }
  }, [projectId, config.productGranularity]);

  useEffect(() => { void load(); }, [load]);

  const createPlan = async (planKind: IncentivePlanKind) => {
    setCreating(true);
    setError(null);
    try {
      const res = await incentiveAPI.createPlan(projectId, {
        name: `Plan ${plans.length + 1}`, scopeType: 'store', active: true, planKind,
      });
      setAskKind(null);
      await load();
      setEditingId(res.data.data.id);
      setView('plans');
    } catch (err: any) {
      setAskKind(null);
      setError(err.response?.data?.message || 'Could not create the plan.');
    } finally {
      setCreating(false);
    }
  };

  const removePlan = async () => {
    if (!deleting) return;
    try {
      await incentiveAPI.deletePlan(projectId, deleting.id);
      setDeleting(null);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not delete the plan.');
    }
  };

  /**
   * Switch a plan on or off from its row.
   *
   * It lives here rather than in the editor because it is a thing done TO a plan,
   * like reordering or deleting it — not one of the plan's own settings. Seeing
   * which plans are live, and changing that, is then one screen instead of
   * opening each one in turn.
   */
  const togglePlan = async (plan: IncentivePlan) => {
    setError(null);
    // Optimistic like movePlan: the row restyles at once and reloads behind it.
    setPlans((prior) => prior.map((p) => (p.id === plan.id ? { ...p, active: !p.active } : p)));
    try {
      await incentiveAPI.setPlanActive(projectId, plan.id, !plan.active);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not switch that plan.');
    }
    await load();
  };

  /** Move a plan up or down the try-order. The first match wins, so this decides
   *  who is paid under what. */
  const movePlan = async (planId: string, direction: -1 | 1) => {
    const ordered = [...plans].sort((a, b) => a.displayOrder - b.displayOrder);
    const i = ordered.findIndex((p) => p.id === planId);
    const j = i + direction;
    if (i < 0 || j < 0 || j >= ordered.length) return;
    [ordered[i], ordered[j]] = [ordered[j], ordered[i]];
    // Optimistic: the list reorders immediately and reloads behind it, because a
    // half-second lag on an arrow button reads as a broken button.
    setPlans(ordered.map((p, k) => ({ ...p, displayOrder: k })));
    try {
      await incentiveAPI.reorderPlans(projectId, ordered.map((p) => p.id));
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not reorder the plans.');
      await load();
    }
  };

  const downloadRun = async () => {
    try {
      const res = await incentiveAPI.download(projectId, month);
      downloadXlsx(`incentive_${month}.xlsx`, res.data);
    } catch (err: unknown) {
      // The sheet is asked for as a file, so a refusal's reason (the designation
      // multiplier could not be applied, say) arrives inside a Blob.
      setError(await blobErrorMessage(err, 'Could not build the payout sheet.'));
    }
  };

  if (loading) {
    return (
      <BlockLoader className="rounded-xl border border-gray-200 bg-white py-20" />
    );
  }

  const editing = plans.find((p) => p.id === editingId) ?? null;
  if (editing) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => { setEditingId(null); void load(); }}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          All plans
        </button>
        <IncentivePlanEditor
          projectId={projectId}
          plan={editing}
          coordinates={coordinates}
          salesFields={salesFields}
          storeFields={storeFields}
          employeeFields={employeeFields}
          onSaved={load}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <PageErrorBanner message={error} />}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          {([['payout', 'Payout', Coins], ['plans', 'Plans', ListChecks]] as const).map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${
                view === key ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {view === 'payout' ? (
            <button
              type="button"
              onClick={downloadRun}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              Download the payout sheet
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setAskKind({ kind: null })}
              disabled={creating}
              className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add a plan
            </button>
          )}
        </div>
      </div>

      {/* The project-wide setting on top of every plan, above the plans themselves. */}
      {view === 'plans' && <DesignationMultiplierCard projectId={projectId} month={month} />}

      {view === 'payout' ? (
        <IncentivePayout projectId={projectId} month={month} planCount={plans.length} />
      ) : plans.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
          <h3 className="text-base font-medium text-gray-700">No incentive plans yet</h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">
            A plan says how sales are paid: a grid of amounts by % of target, or a rate for every
            unit sold. Add one, and everyone&apos;s payout is worked out from the targets and sales
            already in this module.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Covers</th>
                <th className="px-4 py-3">How it pays</th>
                <th className="px-4 py-3">New joiner guarantee</th>
                <th className="px-4 py-3">Live</th>
                <th className=" px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...plans].sort((a, b) => a.displayOrder - b.displayOrder).map((p, i, ordered) => (
                <tr
                  key={p.id}
                  className={`cursor-pointer hover:bg-gray-50 ${p.active ? '' : 'bg-gray-50/60'}`}
                  onClick={() => setEditingId(p.id)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{p.name}</div>
                    {p.description && <div className="text-xs text-gray-500">{p.description}</div>}
                    {!p.active && (
                      <span className="mt-1 inline-block rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">
                        Off
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {p.fieldKey
                      ? `${p.fieldLabel ?? p.fieldKey} ${p.matchOp === 'eq' ? 'is' : p.matchOp} ${
                        Array.isArray(p.matchValue) ? p.matchValue.join(', ') : String(p.matchValue ?? '')}`
                      : <span className="text-gray-500">Everyone not matched above</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {isPerUnitPlan(p) ? describePerUnitPlan(p) : (
                      <>
                        {p.bands.length} band{p.bands.length === 1 ? '' : 's'} ·{' '}
                        {p.products.length} product{p.products.length === 1 ? '' : 's'}
                        {p.rules.length > 0 && ` · ${p.rules.length} rule${p.rules.length === 1 ? '' : 's'}`}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {p.newJoinerMonths
                      ? `Under ${p.newJoinerMonths} months → at least ${formatMoney(p.newJoinerAmount)}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {p.effectiveFrom || p.effectiveTo
                      ? `${p.effectiveFrom?.slice(0, 7) ?? 'always'} → ${p.effectiveTo?.slice(0, 7) ?? 'open'}`
                      : 'Every month'}
                  </td>
                  <td className="px-4 py-3">
                    <TableActionGroup>
                      <TableActionButton action="moveUp" label="Try this plan earlier"
                        type="button"
                        title="Try this plan earlier"
                        disabled={i === 0}
                        onClick={(e) => { e.stopPropagation(); void movePlan(p.id, -1); }} />
                      <TableActionButton action="moveDown" label="Try this plan later"
                        type="button"
                        title="Try this plan later"
                        disabled={i === ordered.length - 1}
                        onClick={(e) => { e.stopPropagation(); void movePlan(p.id, 1); }} />
                      <TableActionButton
                        action={p.active ? 'deactivate' : 'activate'}
                        label={p.active ? 'Switch this plan off' : 'Switch this plan on'}
                        type="button"
                        title={p.active
                          ? 'Switch this plan off — nobody is paid under it'
                          : 'Switch this plan on'}
                        onClick={(e) => { e.stopPropagation(); void togglePlan(p); }} />
                      <TableActionButton action="edit" label="Edit"
                        type="button"
                        title="Edit"
                        onClick={(e) => { e.stopPropagation(); setEditingId(p.id); }} />
                      <TableActionButton action="delete" label="Delete"
                        type="button"
                        title="Delete"
                        onClick={(e) => { e.stopPropagation(); setDeleting(p); }} />
                    </TableActionGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
            Plans are tried top to bottom and the first whose condition matches is the one somebody
            is paid under — so put the specific ones above the catch-all.
          </p>
        </div>
      )}

      <Modal
        isOpen={askKind !== null}
        onClose={() => { if (!creating) setAskKind(null); }}
        title="Add a plan"
        size="lg"
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <button type="button" onClick={() => setAskKind(null)} disabled={creating}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
              Cancel
            </button>
            <button
              type="button"
              disabled={creating || !askKind?.kind}
              onClick={() => { if (askKind?.kind) void createPlan(askKind.kind); }}
              className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              Add the plan
            </button>
          </div>
        }
      >
        <p className="text-sm font-medium text-gray-900">How does it pay?</p>
        <p className="mt-0.5 text-xs text-gray-500">Can&apos;t be changed later.</p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {PLAN_KINDS.map(({ kind, title, detail, Icon }) => {
            const picked = askKind?.kind === kind;
            return (
              <button
                key={kind}
                type="button"
                aria-pressed={picked}
                onClick={() => setAskKind({ kind })}
                className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
                  picked ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-100' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${picked ? 'text-primary-600' : 'text-gray-400'}`} />
                <span>
                  <span className="block text-sm font-semibold text-gray-900">{title}</span>
                  <span className="mt-0.5 block text-xs text-gray-600">{detail}</span>
                </span>
              </button>
            );
          })}
        </div>
      </Modal>

      <ConfirmDeleteModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={removePlan}
        isSubmitting={false}
        title="Delete this plan?"
        entityLabel="incentive plan"
        entityName={deleting?.name ?? ''}
        consequenceNote={
          'Its amounts, rates and rules go with it, and every payout derived from it — including '
          + 'for months already closed — recalculates to nothing. Switch it off instead if you only '
          + 'want it to stop applying from now on.'
        }
      />
    </div>
  );
}
